import { FikoParametreDeposu } from './FikoParametreDeposu';
import { FikoBagimlilikGrafi } from './FikoBagimlilikGrafi';
import { FikoIfadeMotoru } from './FikoIfadeMotoru';
import { FikoYTId } from '@/src/service/fikoyt-ui/helpers/FikoYTId';
import { IFikoParametre, ParamTip, IFikoKonfig, IFikoKontrol } from './IFikoKnowledge';

export class FikoKnowledgeYonetici {
  depo = new FikoParametreDeposu();
  mode: 'oto' | 'manuel' = 'oto';
  private kirli = new Set<string>();
  /** rebuild köprüsü — FikoModellemeYonetici enjekte eder. idx: en erken etkilenen feature index. */
  rebuildKopru: (baslangicIndex?: number) => void = () => {};
  /** Ağaç yapısı değişti (grup/param ekle/sil/taşı) — FikoModellemeYonetici enjekte eder. */
  agacDegisti: () => void = () => {};
  /** ad → feature index çözümü (FikoModellemeYonetici enjekte eder). */
  featIndexCoz: (featId: string) => number = () => 0;
  /** f(x) editör dialogunu aç (FikoModellemeYonetici/Fiko3DComponent enjekte eder). */
  fxAc: (paramId: string) => void = () => {};

  get kirliVarMi(): boolean { return this.kirli.size > 0; }

  /** Reaktivite: parametre değişince tüm görünümler (dialog + ağaç) yenilensin. */
  private _dinleyiciler = new Set<() => void>();
  abone(fn: () => void): () => void { this._dinleyiciler.add(fn); return () => this._dinleyiciler.delete(fn); }
  yayinla(): void { this._dinleyiciler.forEach(f => { try { f(); } catch { } }); }

  konfigler: IFikoKonfig[] = [];
  kontroller: IFikoKontrol[] = [];

  /** Mevcut (formülsüz) parametre değerlerini adlı konfig olarak anlık görüntüle. */
  konfigEkle(ad: string): IFikoKonfig {
    const degerler: Record<string, any> = {};
    for (const p of this.depo.parametreler) if (!p.formul) degerler[p.ad] = p.deger;
    const k: IFikoKonfig = { id: new FikoYTId().toString(), ad, degerler };
    this.konfigler.push(k); return k;
  }
  /** Konfigi uygula: kayıtlı değerleri (formülsüz) parametrelere yaz → çöz → rebuild. */
  konfigUygula(id: string): void {
    const k = this.konfigler.find(x => x.id === id); if (!k) return;
    for (const [ad, deg] of Object.entries(k.degerler)) { const p = this.depo.getAd(ad); if (p && !p.formul) p.deger = deg as any; }
    this.cozumle(); this.rebuildKopru(0); this.yayinla();
  }
  konfigSil(id: string): void { this.konfigler = this.konfigler.filter(k => k.id !== id); }

  kontrolEkle(ad: string, ifade: string): IFikoKontrol {
    const k: IFikoKontrol = { id: new FikoYTId().toString(), ad, ifade };
    this.kontroller.push(k); return k;
  }
  kontrolSil(id: string): void { this.kontroller = this.kontroller.filter(k => k.id !== id); }
  /** Her kontrolü değerlendir: gecti = ifade truthy. Hata → gecti false + mesaj. */
  kontrolDegerlendir(): { id: string; ad: string; ifade: string; gecti: boolean; mesaj: string }[] {
    const coz = (a: string) => this.depo.getAd(a)?.deger;
    return this.kontroller.map(k => {
      try { const r = FikoIfadeMotoru.degerlendir(k.ifade, coz); const gecti = !!r.deger; return { id: k.id, ad: k.ad, ifade: k.ifade, gecti, mesaj: gecti ? 'Tamam' : 'İhlal' }; }
      catch (e: any) { return { id: k.id, ad: k.ad, ifade: k.ifade, gecti: false, mesaj: e?.message || 'Hata' }; }
    });
  }

  /** Tüm parametreleri topolojik sırada çöz: formülleri hesapla, deger güncelle, hata işaretle. */
  cozumle(): void {
    const bag: Record<string, string[]> = {};
    for (const p of this.depo.parametreler) bag[p.ad] = p.formul ? FikoIfadeMotoru.referanslar(p.formul) : [];
    const grafik = new FikoBagimlilikGrafi(bag);
    const donguVar = grafik.donguVarMi();
    const sira = grafik.topolojikSira();
    const coz = (ad: string) => this.depo.getAd(ad)?.deger;
    const islenmis = new Set<string>();
    for (const ad of sira) {
      const p = this.depo.getAd(ad); if (!p) continue; islenmis.add(ad);
      p.hata = undefined;
      if (!p.formul) continue;
      try { p.deger = this.tipDonustur(FikoIfadeMotoru.degerlendir(p.formul, coz).deger, p.tip); }
      catch (e: any) { p.hata = e?.message || 'Formül hatası'; }
    }
    if (donguVar) for (const p of this.depo.parametreler) if (p.formul && !islenmis.has(p.ad)) p.hata = 'Döngüsel bağımlılık';
  }

  private tipDonustur(v: number | boolean | string, tip: ParamTip): number | boolean | string {
    if (tip === 'Tamsayi') return Math.round(v as number);
    if (tip === 'Boolean') return !!v;
    if (tip === 'Metin') return String(v);
    return v;
  }

  /** Parametre değeri/formülü değişti → çöz + (oto) rebuild ya da (manuel) kirli işaretle. */
  parametreGuncelle(id: string, yeni: { deger?: any; formul?: string } | any): void {
    const p = this.depo.getId(id); if (!p) return;
    if (yeni && typeof yeni === 'object' && ('deger' in yeni || 'formul' in yeni)) {
      if ('formul' in yeni) p.formul = yeni.formul || undefined;
      if ('deger' in yeni && !p.formul) p.deger = yeni.deger;
    } else { if (!p.formul) p.deger = yeni; }
    this.cozumle();
    if (this.mode === 'oto') this.tetikle([p]); else { this.kirli.add(id); }
    this.yayinla();
  }

  /** Manuel modda biriken değişiklikleri uygula. */
  guncelle(): void {
    if (!this.kirli.size) return;
    const degisen = [...this.kirli].map(id => this.depo.getId(id)).filter(Boolean) as IFikoParametre[];
    this.kirli.clear(); this.cozumle(); this.tetikle(degisen); this.yayinla();
  }

  /** Değişen parametrelerden en erken etkilenen feature index'ini bulup rebuild köprüsünü çağır. */
  private tetikle(degisen: IFikoParametre[]): void {
    const adlar = degisen.map(p => p.ad);
    const bag: Record<string, string[]> = {};
    for (const p of this.depo.parametreler) bag[p.ad] = p.formul ? FikoIfadeMotoru.referanslar(p.formul) : [];
    const etkilenen = new Set<string>([...adlar, ...new FikoBagimlilikGrafi(bag).downstream(adlar)]);
    let minIdx = Infinity;
    for (const ad of etkilenen) { const p = this.depo.getAd(ad); if (p?.feat) minIdx = Math.min(minIdx, this.featIndexCoz(p.feat.featId)); }
    this.rebuildKopru(isFinite(minIdx) ? minIdx : 0);
  }
}
